import { ArrowDown, ArrowUp } from 'lucide-react';
import { useState } from 'react';

import { DataTable, DataTableCell, DataTableHead, DataTableRow } from '@/components/table/DataTable';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { type AiRoute, aiApi, durationLabel, moved, num, preferredProvider, priceLabel, providerOptions, regionLabel, routesByModel } from '@/lib/aiAdmin';
import { formatCount } from '@/lib/format';

import { CENTER, TITLE, columns, confirmedWrite, SectionLabel, Toggle } from './common';

const APPLIES = 'It applies to every organization within about 30 seconds.';

/** Each model with its routes in order, the preferred provider as a dropdown, and a switch for each route. */
export function ModelsTab({ routes, onRoutes, reload }: { routes: AiRoute[]; onRoutes: (routes: AiRoute[]) => void; reload: () => void }) {
  const [busy, setBusy] = useState<string | undefined>();

  /** Shows the order `ai` answered, then reads the routes again for the health numbers. */
  const applyOrder = (modelId: string, ids: string[] | undefined) => {
    if (ids?.length) {
      const rank = new Map(ids.map((id, i) => [id, i]));
      onRoutes(routes.map((route) => (route.modelId === modelId ? { ...route, priority: rank.get(route.id) ?? route.priority } : route)));
    }
    reload();
  };

  const write = async (key: string, question: string, run: () => Promise<{ routeIds?: string[] } | void>, modelId: string, done: string) => {
    setBusy(key);
    let ids: string[] | undefined;
    const ok = await confirmedWrite(question, async () => void (ids = (await run())?.routeIds), done, 'The change failed');
    if (ok) applyOrder(modelId, ids);
    setBusy(undefined);
  };

  const setEnabled = async (route: AiRoute, enabled: boolean) => {
    setBusy(route.id);
    const ok = await confirmedWrite(
      `Turn ${route.id} ${enabled ? 'on' : 'off'}? ${APPLIES}`,
      () => aiApi.setRouteEnabled(route.id, enabled),
      `${route.id} is ${enabled ? 'on' : 'off'}`,
      'The change failed',
    );
    if (ok) onRoutes(routes.map((candidate) => (candidate.id === route.id ? { ...candidate, enabled } : candidate)));
    setBusy(undefined);
  };

  return (
    <div className="space-y-8">
      {routesByModel(routes).map(({ modelId, routes: list }) => {
        const ids = list.map((route) => route.id);
        const current = preferredProvider(list);
        return (
          <section key={modelId}>
            <SectionLabel
              actions={
                <label className="flex items-center gap-2 text-xs text-muted-foreground">
                  Preferred provider
                  <Select
                    value={current}
                    disabled={busy !== undefined}
                    onValueChange={(provider) =>
                      provider !== current &&
                      void write(
                        modelId,
                        `Prefer ${provider} for ${modelId}? Its routes move to the top and keep their order. ${APPLIES}`,
                        () => aiApi.setOrder(modelId, { preferredProvider: provider }),
                        modelId,
                        `${modelId} now prefers ${provider}`,
                      )
                    }
                  >
                    <SelectTrigger size="sm" className="w-44 text-foreground" aria-label={`Preferred provider for ${modelId}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {providerOptions(list).map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </label>
              }
            >
              {modelId}
            </SectionLabel>
            <DataTable>
              <DataTableHead columns={columns(['Route', 'On', 'Order', 'Price in / out per 1M', 'Last hour'])} />
              <tbody>
                {list.map((route, index) => (
                  <DataTableRow key={route.id}>
                    <DataTableCell className={TITLE}>
                      <span className="mr-2 text-muted-foreground tabular-nums">{index + 1}</span>
                      {route.id}
                      <span className="mt-0.5 block pl-5 text-xs font-normal text-muted-foreground">
                        {regionLabel(route.regionClass)}, {route.retentionClass}
                      </span>
                    </DataTableCell>
                    <DataTableCell className={CENTER}>
                      <Toggle checked={route.enabled === true} label={`${route.id} on`} disabled={busy !== undefined} onChange={(next) => void setEnabled(route, next)} />
                    </DataTableCell>
                    <DataTableCell className={CENTER}>
                      <div className="inline-flex gap-1">
                        {([-1, 1] as const).map((step) => (
                          <Button
                            key={step}
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="h-8 w-8 px-0"
                            aria-label={`Move ${route.id} ${step < 0 ? 'up' : 'down'}`}
                            disabled={busy !== undefined || moved(ids, index, step) === ids}
                            onClick={() =>
                              void write(
                                modelId,
                                `Move ${route.id} ${step < 0 ? 'up' : 'down'}? ${APPLIES}`,
                                () => aiApi.setOrder(modelId, { routeIds: moved(ids, index, step) }),
                                modelId,
                                `${modelId} has a new order`,
                              )
                            }
                          >
                            {step < 0 ? <ArrowUp className="h-4 w-4" aria-hidden="true" /> : <ArrowDown className="h-4 w-4" aria-hidden="true" />}
                          </Button>
                        ))}
                      </div>
                    </DataTableCell>
                    <DataTableCell className={`${CENTER} tabular-nums`}>
                      {priceLabel(route.price?.inputMicrosPerMtok)} / {priceLabel(route.price?.outputMicrosPerMtok)}
                    </DataTableCell>
                    <DataTableCell className={`${CENTER} tabular-nums`}>
                      {formatCount(num(route.health?.calls))} calls, {formatCount(num(route.health?.errors))} {num(route.health?.errors) === 1 ? 'error' : 'errors'}
                      <span className="mt-0.5 block text-xs text-muted-foreground">p50 {durationLabel(route.health?.p50LatencyMs)}</span>
                    </DataTableCell>
                  </DataTableRow>
                ))}
              </tbody>
            </DataTable>
          </section>
        );
      })}
      {routes.length === 0 ? <p className="text-sm text-muted-foreground">ai has no routes.</p> : null}
    </div>
  );
}
